import React, { useCallback, useEffect, useId, useRef, useState } from "react";

// ─── Rich text: **text** → highlighted <mark> ──────────────────────────────
export function Rich({ text, as: Tag = "span", className }) {
  if (text == null) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <Tag className={className}>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <mark key={i} className="shs-mark" style={{ "--d": `${0.35 + i * 0.12}s` }}>{p.slice(2, -2)}</mark>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </Tag>
  );
}

// ─── Count-up hook (used for the "why it matters" numbers and the score) ───
export function useCountUp(target, { duration = 1400, start = true } = {}) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!start) return undefined;
    const to = Number(target) || 0;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(to * eased));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration, start]);
  return value;
}

export const fmtIN = (n) => Number(n || 0).toLocaleString("en-IN");

// ─── Bin illustration (SVG). `colour` is the bin's hex; `open` lifts the lid. ─
export function BinArt({ colour = "#22a447", dark = "#157a33", label = "", open = false, className = "", style }) {
  // Unique per instance: several bins share a slide, and an id derived from a
  // (possibly empty) label collided so every bin painted with the first gradient.
  const gid = `shsBinG${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg
      className={`shs-bin ${open ? "is-open" : ""} ${className}`}
      viewBox="0 0 72 86"
      aria-hidden="true"
      style={style}
    >
      <defs>
        <linearGradient id={gid} x1="0" x2="1">
          <stop offset="0" stopColor={colour} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
      </defs>
      {/* body */}
      <path d="M12 22h48l-4 56a6 6 0 0 1-6 6H22a6 6 0 0 1-6-6z" fill={`url(#${gid})`} />
      <path d="M22 30v46M36 30v46M50 30v46" stroke="rgba(255,255,255,0.28)" strokeWidth="3" strokeLinecap="round" />
      {/* recycling mark: three chasing arrows */}
      <g transform="translate(36 50)" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-9.5 3.5 A11 11 0 0 1 -3 -9" />
        <path d="M3 -9 A11 11 0 0 1 9.5 3.5" />
        <path d="M9.5 3.5 A11 11 0 0 1 -9.5 3.5" strokeDasharray="14 40" />
        <path d="M-3 -9 l-4.5 -1.5 M-3 -9 l1.5 4.5" />
        <path d="M9.5 3.5 l1.5 -4.5 M9.5 3.5 l-4.5 1.5" />
        <path d="M-9.5 3.5 l4.5 1.5 M-9.5 3.5 l-1.5 -4.5" />
      </g>
      {label && (
        <text x="36" y="80" textAnchor="middle" className="shs-bin__label">
          {label}
        </text>
      )}
    </svg>
  );
}

// ─── Animated campus scene: sky, sun, clouds, ground, four bins with drops ──
// Each item falls into the bin it actually belongs in (Rule 3(1) streams), so
// the animation teaches the right thing: fruit and leaves to the green bin,
// paper/plastic/glass to the blue one, sanitary waste to red, and batteries and
// bulbs to the black special-care bin. Keyed by bin, never spread at random.
const DROPS = {
  wet: ["🍌", "🍎", "🥬"],
  dry: ["📰", "🧴", "🥫"],
  sanitary: ["🩹", "🧻"],
  special: ["🔋", "💡"],
};

export function Scene({ bins, compact = false }) {
  return (
    <div className={`shs-scene ${compact ? "shs-scene--compact" : ""}`} aria-hidden="true">
      <div className="shs-scene__sun" />
      <div className="shs-scene__cloud shs-scene__cloud--1" />
      <div className="shs-scene__cloud shs-scene__cloud--2" />
      <div className="shs-scene__hill shs-scene__hill--1" />
      <div className="shs-scene__hill shs-scene__hill--2" />
      <div className="shs-scene__ground" />
      <div className="shs-scene__bins">
        {bins.map((b, i) => (
          // The items sit INSIDE their bin's slot, so each one always lands in
          // that bin whatever the width or the number of bins.
          <span key={b.key} className="shs-binslot">
            {(DROPS[b.key] || []).map((d, k) => (
              <span key={d} className="shs-drop" style={{ "--delay": `${i * 0.55 + k * 3.4}s` }}>{d}</span>
            ))}
            <BinArt colour={b.hex} dark={b.dark} label={b.name.split(" ")[0].toUpperCase()} style={{ "--i": i }} />
          </span>
        ))}
      </div>
      <span className="shs-scene__leaf shs-scene__leaf--1">🍃</span>
      <span className="shs-scene__leaf shs-scene__leaf--2">🍃</span>
    </div>
  );
}

// ─── Circular progress ring ────────────────────────────────────────────────
export function Ring({ pct = 0, size = 120, stroke = 12, children, colour }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const gid = `shsRing${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <div className="shs-ringwrap" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <defs>
          <linearGradient id={gid} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor={colour || "#1b7f3b"} />
            <stop offset="0.6" stopColor={colour || "#2fb457"} />
            <stop offset="1" stopColor="#f28c28" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(27,127,59,0.12)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={`url(#${gid})`} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0, Math.min(1, pct)))}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="shs-ringwrap__inner">{children}</div>
    </div>
  );
}

// ─── Growing seedling (compost timeline) ───────────────────────────────────
export function Sprout({ className = "" }) {
  return (
    <svg className={`shs-sprout ${className}`} viewBox="0 0 80 100" aria-hidden="true">
      <ellipse cx="40" cy="92" rx="30" ry="6" fill="#8a5a2f" />
      <path className="shs-sprout__stem" d="M40 90 C40 70 40 55 40 38" stroke="#2f8f4a" strokeWidth="5" strokeLinecap="round" fill="none" />
      <path className="shs-sprout__leaf shs-sprout__leaf--l" d="M40 58 C28 58 18 48 18 36 C30 36 40 46 40 58Z" fill="#43b563" />
      <path className="shs-sprout__leaf shs-sprout__leaf--r" d="M40 46 C52 46 64 36 64 22 C50 22 40 32 40 46Z" fill="#63cf7f" />
      <circle className="shs-sprout__bud" cx="40" cy="36" r="5" fill="#f28c28" />
    </svg>
  );
}

// ─── 3-D tilt on hover (posters) ───────────────────────────────────────────
export function useTilt(max = 10) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max).toFixed(2)}deg) translateY(-6px) scale(1.02)`;
    el.style.setProperty("--gx", `${(x + 0.5) * 100}%`);
    el.style.setProperty("--gy", `${(y + 0.5) * 100}%`);
  }, [max]);
  const onLeave = useCallback(() => {
    const el = ref.current;
    if (el) el.style.transform = "";
  }, []);
  return { ref, onMouseMove: onMove, onMouseLeave: onLeave };
}

// ─── Confetti (canvas) — fired once on the result screen ───────────────────
const CONFETTI_COLOURS = ["#1b7f3b", "#2fb457", "#f28c28", "#1e6fd9", "#ffd166", "#ffffff"];
// `colours` defaults to a module constant: an inline default array is a new
// reference every render, which re-armed the effect (and restarted the burst)
// on every count-up frame.
export function Confetti({ run = true, duration = 3800, colours = CONFETTI_COLOURS }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!run) return undefined;
    const canvas = ref.current;
    if (!canvas) return undefined;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    const resize = () => {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    };
    resize();
    const N = 180;
    const parts = Array.from({ length: N }, () => ({
      x: canvas.width * (0.3 + Math.random() * 0.4),
      y: canvas.height * 0.35,
      vx: (Math.random() - 0.5) * 16,
      vy: -Math.random() * 15 - 4,
      w: 6 + Math.random() * 6,
      h: 8 + Math.random() * 8,
      r: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      c: colours[Math.floor(Math.random() * colours.length)],
    }));
    const t0 = performance.now();
    let raf;
    const draw = (now) => {
      const t = now - t0;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach((p) => {
        p.vy += 0.35;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.r += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.globalAlpha = Math.max(0, 1 - t / duration);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (t < duration) raf = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [run, duration, colours]);
  return <canvas ref={ref} className="shs-confetti" aria-hidden="true" />;
}

// ─── Floating leaves for the backdrop ──────────────────────────────────────
export function Leaves({ count = 16 }) {
  const leaves = React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        x: `${(i * 97) % 100}%`,
        delay: `${(i * 1.3) % 12}s`,
        dur: `${12 + ((i * 7) % 9)}s`,
        rot: `${(i * 53) % 360}deg`,
        scale: 0.7 + ((i * 31) % 10) / 12,
      })),
    [count]
  );
  return (
    <div className="shs-leaves" aria-hidden="true">
      {leaves.map((l, i) => (
        <span
          key={i}
          className="shs-leaf"
          style={{ "--x": l.x, "--delay": l.delay, "--dur": l.dur, "--rot": l.rot, transform: `scale(${l.scale})` }}
        />
      ))}
    </div>
  );
}

export const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};
